import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { XMarkIcon } from '@heroicons/react/24/outline';

const PaperSizePanel = () => {
  const { 
    isPaperPanelOpen, 
    setIsPaperPanelOpen,
    uploadedImages
  } = useApp();
  
  // Usar a primeira imagem como referência para dimensões
  const firstImage = uploadedImages[0];
  const currentWidth = firstImage?.image?.width || 0;
  const currentHeight = firstImage?.image?.height || 0;
  
  const [showCustom, setShowCustom] = useState(false);
  const [customWidth, setCustomWidth] = useState(currentWidth);
  const [customHeight, setCustomHeight] = useState(currentHeight);

  const templates = [
    { name: 'Instagram Story', width: 1080, height: 1920, description: '1080 × 1920 px' },
    { name: 'Instagram Post', width: 1080, height: 1080, description: '1080 × 1080 px' },
    { name: 'Facebook Post', width: 1200, height: 630, description: '1200 × 630 px' },
    { name: 'Twitter Post', width: 1200, height: 675, description: '1200 × 675 px' },
    { name: 'A4 Retrato', width: 2480, height: 3508, description: '210 × 297 mm (300 DPI)' },
    { name: 'A4 Paisagem', width: 3508, height: 2480, description: '297 × 210 mm (300 DPI)' },
    { name: 'Quadrado 2048', width: 2048, height: 2048, description: '2048 × 2048 px' },
    { name: 'Personalizado', width: 0, height: 0, description: 'Defina suas dimensões' }
  ];

  const handleTemplateSelect = (template) => {
    if (template.name === 'Personalizado') {
      setShowCustom(true);
    } else {
      // Para o MVP, não há mais baseLayer - apenas fechar o painel
      // O tamanho do papel é definido pela primeira imagem
      setIsPaperPanelOpen(false);
    }
  };

  const handleCustomSize = () => {
    if (customWidth > 0 && customHeight > 0) {
      // Para o MVP, não há mais baseLayer - apenas fechar o painel
      // O tamanho do papel é definido pela primeira imagem
      setShowCustom(false);
      setIsPaperPanelOpen(false);
    }
  };

  if (!isPaperPanelOpen) return null;

  return (
    <div className="fixed right-6 top-6 bottom-6 w-80 z-30 pointer-events-none animate-fadeIn">
      <div className="backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col h-full pointer-events-auto" style={{ backgroundColor: '#101f00' }}>
        {/* Header */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-800">Tamanho do Papel</h3>
            <button
              onClick={() => setIsPaperPanelOpen(false)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-4">
          {!showCustom ? (
            <div className="space-y-3">
              {templates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateSelect(template)}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    currentWidth === template.width && currentHeight === template.height
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  style={{
                    borderColor: currentWidth === template.width && currentHeight === template.height ? '#7eb715' : undefined,
                    backgroundColor: currentWidth === template.width && currentHeight === template.height ? '#f4f9e8' : undefined
                  }}
                >
                  <div className="font-medium text-gray-800">{template.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{template.description}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Largura (px)
                </label>
                <input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': '#7eb715' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altura (px)
                </label>
                <input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': '#7eb715' }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCustom(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Voltar
                </button>
                <button
                  onClick={handleCustomSize}
                  disabled={customWidth <= 0 || customHeight <= 0}
                  className="flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  style={{ background: '#7eb715' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#638C08'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#7eb715'}
                >
                  Aplicar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info atual */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <h4 className="font-medium text-gray-800 mb-2">Tamanho Atual</h4>
          <p className="text-gray-700 text-sm">
            {firstImage ? `${currentWidth} × ${currentHeight} px` : 'Nenhuma imagem carregada'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaperSizePanel;

